import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Mail, Hash, Pencil, Tags, Search, ArrowUpDown, Settings2 } from "lucide-react";

const EMPTY_FORM = { name: "", sector: "", vat: "", contact_email: "", referents: "", location: "", tags: [], description: "", notes: "" };

export default function Companies() {
  const { user } = useAuth();
  const isCompanyUser = user?.role === "company_user";
  const [companies, setCompanies] = useState([]);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name_asc");
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [tagName, setTagName] = useState("");
  const [editingTag, setEditingTag] = useState(null);

  const load = () => Promise.all([api.get("/companies"), api.get("/company-tags")])
    .then(([companiesResponse, tagsResponse]) => { setCompanies(companiesResponse.data); setTagCatalog(tagsResponse.data); })
    .catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);

  const openEditor = (company = null) => {
    setEditingCompany(company);
    const legacyTags = typeof company?.tags === "string" ? company.tags.split(",").map((tag) => tag.trim()).filter(Boolean) : (company?.tags || []);
    setForm(company ? { ...EMPTY_FORM, ...company, tags: legacyTags } : { ...EMPTY_FORM });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, form);
        toast.success("Azienda aggiornata");
      } else {
        await api.post("/companies", form);
        toast.success("Azienda creata");
      }
      setForm({ ...EMPTY_FORM });
      setEditingCompany(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editingCompany ? "Errore aggiornamento" : "Errore creazione"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/companies/${id}`); toast.success("Azienda eliminata"); load(); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const saveTag = async (e) => {
    e.preventDefault();
    try {
      if (editingTag) await api.put(`/company-tags/${editingTag.id}`, { name: tagName });
      else await api.post("/company-tags", { name: tagName });
      toast.success(editingTag ? "TAG aggiornato" : "TAG creato");
      setTagName(""); setEditingTag(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio TAG"); }
  };

  const removeTag = async (tag) => {
    try { await api.delete(`/company-tags/${tag.id}`); toast.success("TAG eliminato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Il TAG è ancora utilizzato"); }
  };

  const visibleCompanies = companies
    .filter((company) => {
      const query = search.trim().toLowerCase();
      const companyTags = Array.isArray(company.tags) ? company.tags : (company.tags || "").split(",").map((tag) => tag.trim());
      const matchesSearch = !query || [company.name, company.sector, company.vat, company.contact_email, company.location, company.description]
        .filter(Boolean).some((value) => value.toLowerCase().includes(query));
      return matchesSearch && (tagFilter === "all" || companyTags.includes(tagFilter));
    })
    .sort((a, b) => {
      if (sortBy === "created_desc") return (b.created_at || "").localeCompare(a.created_at || "");
      if (sortBy === "created_asc") return (a.created_at || "").localeCompare(b.created_at || "");
      if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "", "it", { sensitivity: "base" });
      return (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" });
    });

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Portfolio Clienti</div>
          <h1 className="font-display font-black text-4xl md:text-5xl">Aziende</h1>
          <p className="text-slate-500 mt-2 text-sm">Gestisci le aziende soggette ad audit.</p>
        </div>
         <Dialog open={open} onOpenChange={setOpen}>
          <div className="flex items-center gap-2">
          <Button type="button" onClick={() => setTagManagerOpen(true)} className="h-11 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 px-4"><Settings2 className="h-4 w-4" /> Gestisci TAGS</Button>
          {!isCompanyUser && <DialogTrigger asChild>
              <Button data-testid="new-company-btn" onClick={() => openEditor()} className="h-11 bg-slate-900 hover:bg-slate-800 px-5">
                <Plus className="h-4 w-4" /> Nuova Azienda
              </Button>
            </DialogTrigger>}
          </div>
          <DialogContent className="max-w-lg">
            <DialogHeader>
               <DialogTitle className="font-display font-black text-2xl">{editingCompany ? "Modifica Azienda" : "Nuova Azienda"}</DialogTitle>
            </DialogHeader>
             <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Denominazione *</Label>
                <Input data-testid="company-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" placeholder="Es. Rossi SpA" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Settore</Label>
                  <Input data-testid="company-sector-input" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="mt-2 h-11" placeholder="Es. Sanità" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">P.IVA</Label>
                  <Input data-testid="company-vat-input" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} className="mt-2 h-11" placeholder="IT01234567890" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Email di contatto</Label>
                <Input data-testid="company-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-2 h-11" placeholder="privacy@azienda.it" />
              </div>
              <div>
                <div className="flex items-center justify-between"><Label className="text-xs uppercase tracking-wider text-slate-600">TAGS</Label><button type="button" onClick={() => setTagManagerOpen(true)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><Settings2 className="h-3 w-3" /> Gestisci TAGS</button></div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                  {tagCatalog.map((tag) => {
                    const selected = form.tags.includes(tag.name);
                    return <button key={tag.id} type="button" data-testid={`company-tag-${tag.id}`} onClick={() => setForm({ ...form, tags: selected ? form.tags.filter((value) => value !== tag.name) : [...form.tags, tag.name] })} className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${selected ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`} aria-pressed={selected}>{selected ? "✓ " : ""}{tag.name}</button>;
                  })}
                </div>
                <div className="mt-1.5 text-[11px] text-slate-500">Seleziona uno o più TAG del tuo tenant.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Città (per verbale)</Label>
                  <Input data-testid="company-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 h-11" placeholder="Es. Alessandria" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Referenti (per verbale)</Label>
                  <Input data-testid="company-referents-input" value={form.referents} onChange={(e) => setForm({ ...form, referents: e.target.value })} className="mt-2 h-11" placeholder="Nome Cognome email@..." />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Descrizione</Label>
                <Textarea data-testid="company-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" rows={3} placeholder="Descrizione dell'azienda..." />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Note</Label>
                <Textarea data-testid="company-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" rows={3} placeholder="Note interne..." />
              </div>
              <DialogFooter>
               <Button type="submit" data-testid="company-save-btn" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                   {saving ? "Salvataggio…" : editingCompany ? "Aggiorna Azienda" : "Salva Azienda"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
         </Dialog>
      </div>

      {companies.length > 0 && <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="company-filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, P.IVA, settore..." className="h-10 pl-9" />
        </div>
        <select data-testid="company-filter-tag" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
          <option value="all">Tutti i settori</option>
          {tagCatalog.map((tag) => <option key={tag.id} value={tag.name}>{tag.name}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-slate-400" />
          <select data-testid="company-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
            <option value="name_asc">Nome A-Z</option>
            <option value="name_desc">Nome Z-A</option>
            <option value="created_desc">Più recenti</option>
            <option value="created_asc">Meno recenti</option>
          </select>
        </div>
        {(search || tagFilter !== "all") && <button type="button" onClick={() => { setSearch(""); setTagFilter("all"); }} className="h-10 px-2 text-xs font-medium text-slate-500 hover:text-slate-900">Azzera filtri</button>}
      </div>}

      {companies.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg mb-2">Nessuna azienda registrata</div>
          <p className="text-slate-500 text-sm mb-6">{isCompanyUser ? "La tua azienda apparirà qui." : "Aggiungi la prima azienda o aggancia una P.IVA per iniziare."}</p>
           {!isCompanyUser && <Button onClick={() => openEditor()} data-testid="empty-new-company-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Nuova Azienda</Button>}
        </div>
      ) : visibleCompanies.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-sm text-slate-500">Nessuna azienda corrisponde ai filtri.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {visibleCompanies.map((c) => (
             <div key={c.id} data-testid={`company-card-${c.id}`} onDoubleClick={() => openEditor(c)} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm cursor-pointer">
               <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{c.sector || "Settore n/d"}</div>
                </div>
                 <div className="flex items-center gap-1">
                 <button type="button" data-testid={`edit-company-${c.id}`} onClick={() => openEditor(c)} className="p-2 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200" title="Modifica azienda">
                   <Pencil className="h-4 w-4" />
                 </button>
                 <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-company-${c.id}`} disabled={isCompanyUser} className={`p-2 rounded-md text-slate-400 transition-colors duration-200 ${isCompanyUser ? "opacity-30 cursor-not-allowed" : "hover:text-red-600 hover:bg-red-50"}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare "{c.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>Tutti gli audit associati verranno eliminati. Operazione irreversibile.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-company-${c.id}`} onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                 </AlertDialog>
                 </div>
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-slate-500">
                {c.vat && <div className="flex items-center gap-2"><Hash className="h-3 w-3" /> <span className="font-mono-plex">{c.vat}</span></div>}
                {c.contact_email && <div className="flex items-center gap-2"><Mail className="h-3 w-3" /> <span className="truncate">{c.contact_email}</span></div>}
                {c.tags?.length > 0 && <div className="flex items-center gap-2"><Tags className="h-3 w-3 shrink-0" /> <span className="truncate">{Array.isArray(c.tags) ? c.tags.join(", ") : c.tags}</span></div>}
              </div>
              {c.description && <div className="mt-3 text-xs text-slate-600 border-t border-slate-100 pt-3 line-clamp-2">{c.description}</div>}
              {c.notes && <div className="mt-3 text-xs text-slate-600 border-t border-slate-100 pt-3 line-clamp-2">{c.notes}</div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={tagManagerOpen} onOpenChange={setTagManagerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>TAG del tenant</DialogTitle></DialogHeader>
          <form onSubmit={saveTag} className="flex gap-2">
            <Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="Nuovo TAG" required maxLength={80} />
            <Button type="submit" className="bg-slate-900">{editingTag ? "Salva" : "Aggiungi"}</Button>
          </form>
          <div className="space-y-2">
            {tagCatalog.map((tag) => <div key={tag.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm"><span>{tag.name}</span><span className="flex gap-2"><button type="button" onClick={() => { setEditingTag(tag); setTagName(tag.name); }} className="text-slate-500 hover:text-blue-600">Modifica</button><button type="button" onClick={() => removeTag(tag)} className="text-slate-500 hover:text-red-600">Elimina</button></span></div>)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
